import { useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import {
  createTournament,
  selectTournament,
  uploadTournamentLogo,
  getSelectedTournament,
} from "../../redux/slices/tournamentSlice";
import {
  createOrganisation,
  selectOrganisation,
} from "../../redux/slices/organisationSlice";
import { useTournamentCreation } from "../../context/TournamentCreationContext";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import { competitionCreated } from "../../utils/analytics";

const CompetitionFeesRefactored = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { formData, updateField, getCreatePayload, resetForm } = useTournamentCreation();

  const selectedOrganisation = useSelector(
    (state) => state.organisation.selectedOrganisation
  );

  const categories = formData.categories;
  const categoryFees = formData.categoryFees;
  const [isLoading, setIsLoading] = useState(false);

  const updateCategoryFee = (category, value) => {
    updateField("categoryFees", { ...categoryFees, [category]: value });
  };

  const validateInput = () => {
    const feesToCheck = categories.length > 0 ? categories : ["default"];
    for (const cat of feesToCheck) {
      const fee = categoryFees[cat];
      if (fee !== undefined && fee !== "") {
        const value = parseFloat(fee);
        if (Number.isNaN(value) || value < 0) {
          toast.error(`Registration fee for ${cat === "default" ? "the competition" : cat} must be a positive number`);
          return false;
        }
      }
    }
    return true;
  };

  const handleCreate = async () => {
    try {
      if (!validateInput()) return;

      if (!selectedOrganisation?.id) {
        toast.error("Please select an organisation first");
        return;
      }

      setIsLoading(true);

      // 1. Handle mock organisation
      let orgIdToUse = selectedOrganisation.id;
      if (orgIdToUse === "default-org-1") {
        try {
          const orgResult = await dispatch(
            createOrganisation({ name: "Default Organisation" })
          ).unwrap();
          orgIdToUse = orgResult.id;
          dispatch(selectOrganisation({ ...orgResult, isSelected: true }));
        } catch (err) {
          toast.error("Failed to initialize default organisation");
          setIsLoading(false);
          return;
        }
      }

      // 2. Create tournament with all data in one call
      const payload = getCreatePayload(orgIdToUse);
      const createResult = await dispatch(createTournament(payload));

      if (createResult.meta.requestStatus === "rejected") {
        throw new Error(createResult.payload || "Failed to create competition");
      }

      const newTournament = createResult.payload;

      // 3. Select tournament in Redux
      dispatch(selectTournament(newTournament));

      competitionCreated({
        competitionId: newTournament?.id || newTournament?._id,
        sport: newTournament?.sport,
        categoryCount: newTournament?.categories?.length,
      });
      // Meta Pixel: competition-created is the activation conversion for ads.
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'CompetitionCreated', {
          tournament_id: newTournament?.id || newTournament?._id,
        });
      }

      // 3b. Re-fetch full tournament to ensure categories have _id populated
      if (selectedOrganisation?.id) {
        await dispatch(getSelectedTournament(selectedOrganisation.id));
      }

      // 4. Upload logo if provided
      if (formData.logoFile) {
        try {
          const logoFormData = new FormData();
          logoFormData.append("logo", formData.logoFile);
          await dispatch(
            uploadTournamentLogo({
              tournamentId: newTournament.id,
              file: logoFormData,
            })
          ).unwrap();
        } catch (err) {
        }
      }

      resetForm();
      toast.success(`Competition "${formData.name}" created!`);
      navigate(DASHBOARD_STACK.HOME);
    } catch (error) {
      toast.error(error.message || "Failed to create competition");
    } finally {
      setIsLoading(false);
    }
  };

  const renderFeeInput = (category, label) => (
    <div key={category} className="flex flex-col gap-2">
      <label className="block text-[14px] font-normal text-clubbie-white ">
        {label}
      </label>
      <div className="flex items-center gap-[8px] bg-app-surface rounded-3xl px-4 py-2.5">
        <div className="flex items-center gap-[8px]">
          <div className="flex items-center gap-[4px] relative">
            <select
              className="bg-transparent text-app-text-secondary text-sm focus:outline-none cursor-pointer appearance-none pr-[20px]"
              defaultValue="GBP"
            >
              <option value="GBP" className="bg-app-surface text-app-text-secondary">
                GBP
              </option>
            </select>
            <svg
              className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none text-app-text-secondary"
              width="12"
              height="12"
              viewBox="0 0 12 8"
              fill="none"
            >
              <path
                d="M1 1.5L6 6.5L11 1.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="w-px h-5 bg-app-text-secondary"></div>
        </div>
        <div className="flex items-center gap-[8px] flex-1">
          <span className="text-app-text-secondary text-sm">£</span>
          <input
            type="number"
            min="0"
            value={categoryFees[category] || ""}
            onChange={(e) => updateCategoryFee(category, e.target.value)}
            placeholder="Enter fee per team (e.g. £30)"
            className="flex-1 bg-transparent border-none outline-none text-sm text-[#FEFCEB] placeholder-app-text-secondary"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-full bg-[#000F1F] text-[#FEFCEB] p-6 flex flex-col items-center overflow-hidden">
      <div className="w-full max-w-3xl flex-1 flex flex-col gap-[20px] items-center justify-center">
        {/* Header */}
        <div className="flex flex-col gap-[16px] items-center justify-center">
          <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-medium text-[#FEFCEB] text-center tracking-[1px]">Create New Competition</h1>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-3 sm:gap-[28px]">
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Competition details</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Configuration</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Venue details</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] text-[#000F1F] flex items-center justify-center font-semibold text-[14px]">4</div>
              <span className="text-[14px] font-medium text-[#FEFCEB]">Registration fee</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="w-full max-w-[480px] flex flex-col gap-[16px]">
          {categories.length > 0
            ? categories.map((cat) => renderFeeInput(cat, `${cat} fee details`))
            : renderFeeInput("default", "Registration fee")
          }

          <div className="flex items-start gap-[8px]">
            <div className="w-4 h-4 flex items-center justify-center pt-[2px]">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7" stroke="#5FC4EE" strokeWidth="1.5"/>
                <path d="M8 5V8M8 11H8.01" stroke="#5FC4EE" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="text-[#7F8585] text-xs font-normal leading-[1.3]">
              Enter the registration fee for each team (or leave blank if the competition is free).
            </p>
          </div>
        </div>

        {/* Buttons */}
        <div className="w-full max-w-[480px] flex flex-col gap-[12px] items-center justify-center">
          {/* Create Button */}
          <button
            onClick={handleCreate}
            disabled={isLoading}
            className="w-full h-[44px] px-[16px] py-[8px] bg-[#00E7FF] hover:bg-[#00cce6] text-[#021529] text-sm font-medium rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Creating..." : "Create"}
          </button>

          {/* Back Link */}
          <button
            onClick={() => navigate(DASHBOARD_STACK.COMPETITIONS_VENUE)}
            className="h-[32px] px-[8px] py-0 text-app-text-secondary text-sm font-medium rounded-lg hover:text-[#FEFCEB] transition-colors"
          >
            Back to venue details
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetitionFeesRefactored;
